import React, { useState, useEffect } from 'react';
import {
  getDiscordChannels,
  addDiscordChannel,
  updateDiscordChannel,
  deleteDiscordChannel,
  updateChannelNameFromDiscord,
  getDiscordBroadcastMappings,
  setDiscordBroadcastMapping,
  sendFridayPickReminder,
  sendSaturdayPickReminder,
  getDiscordServerInvite,
  updateDiscordServerInvite
} from '../../adminApi';
import styles from './DiscordChannelManager.module.css';

const DiscordChannelManager = () => {
  const [channels, setChannels] = useState([]);
  const [broadcastMappings, setBroadcastMappings] = useState({});
  const [broadcastTypes, setBroadcastTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Form states
  const [newChannel, setNewChannel] = useState({ channelId: '', displayName: '' });
  const [editingChannel, setEditingChannel] = useState(null);
  const [inviteUrl, setInviteUrl] = useState('');
  const [savingInvite, setSavingInvite] = useState(false);

  // Pick reminder states
  const [sendingReminder, setSendingReminder] = useState({ friday: false, saturday: false });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [channelsResponse, mappingsResponse, inviteResponse] = await Promise.all([
        getDiscordChannels(),
        getDiscordBroadcastMappings(),
        getDiscordServerInvite().catch(() => ({ data: { url: '' } }))
      ]);

      setChannels(channelsResponse.data);
      setBroadcastMappings(mappingsResponse.data.mappings);
      setBroadcastTypes(mappingsResponse.data.broadcastTypes);
      setInviteUrl(inviteResponse.data?.url || '');
    } catch (error) {
      console.error('Error loading Discord data:', error);
      setMessage({ type: 'error', text: 'Failed to load Discord settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveInvite = async (e) => {
    e.preventDefault();
    try {
      setSavingInvite(true);
      const response = await updateDiscordServerInvite(inviteUrl.trim());
      setInviteUrl(response.data?.url || '');
      setMessage({ type: 'success', text: 'Server invite link saved' });
    } catch (error) {
      console.error('Error saving Discord server invite:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to save server invite link'
      });
    } finally {
      setSavingInvite(false);
    }
  };

  const handleAddChannel = async (e) => {
    e.preventDefault();
    if (!newChannel.channelId || !newChannel.displayName) {
      setMessage({ type: 'error', text: 'Channel ID and display name are required' });
      return;
    }

    try {
      setSaving(true);
      const response = await addDiscordChannel(newChannel);
      setChannels([...channels, response.data]);
      setNewChannel({ channelId: '', displayName: '' });
      setMessage({ type: 'success', text: 'Channel added successfully' });
    } catch (error) {
      console.error('Error adding channel:', error);
      setMessage({ type: 'error', text: 'Failed to add channel' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateChannel = async (id, updates) => {
    try {
      setSaving(true);
      const response = await updateDiscordChannel(id, updates);
      setChannels(channels.map(channel =>
        channel.id === id ? response.data : channel
      ));
      setEditingChannel(null);
      setMessage({ type: 'success', text: 'Channel updated successfully' });
    } catch (error) {
      console.error('Error updating channel:', error);
      setMessage({ type: 'error', text: 'Failed to update channel' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteChannel = async (id) => {
    if (!window.confirm('Are you sure you want to delete this channel? This will remove all broadcast mappings for this channel.')) {
      return;
    }

    try {
      setSaving(true);
      await deleteDiscordChannel(id);
      setChannels(channels.filter(channel => channel.id !== id));
      setMessage({ type: 'success', text: 'Channel deleted successfully' });
    } catch (error) {
      console.error('Error deleting channel:', error);
      setMessage({ type: 'error', text: 'Failed to delete channel' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateChannelName = async (id) => {
    try {
      setSaving(true);
      const response = await updateChannelNameFromDiscord(id);
      setChannels(channels.map(channel =>
        channel.id === id ? response.data : channel
      ));
      setMessage({ type: 'success', text: 'Channel name updated from Discord' });
    } catch (error) {
      console.error('Error updating channel name:', error);
      setMessage({ type: 'error', text: 'Failed to update channel name from Discord' });
    } finally {
      setSaving(false);
    }
  };

  const handleBroadcastMappingChange = async (broadcastType, channelId) => {
    try {
      setSaving(true);
      await setDiscordBroadcastMapping({
        broadcastType,
        channelId: channelId || null,
        enabled: !!channelId
      });

      setBroadcastMappings(prev => ({
        ...prev,
        [broadcastType]: channelId ? {
          channel_id: channelId,
          display_name: channels.find(c => c.channel_id === channelId)?.display_name,
          channel_name: channels.find(c => c.channel_id === channelId)?.channel_name
        } : null
      }));

      setMessage({ type: 'success', text: 'Broadcast mapping updated successfully' });
    } catch (error) {
      console.error('Error updating broadcast mapping:', error);
      setMessage({ type: 'error', text: 'Failed to update broadcast mapping' });
    } finally {
      setSaving(false);
    }
  };

  const getChannelDisplayName = (channelId) => {
    const channel = channels.find(c => c.channel_id === channelId);
    return channel ? channel.display_name : 'Unknown Channel';
  };

  const sendPickReminder = async (type) => {
    try {
      setSendingReminder(prev => ({ ...prev, [type]: true }));
      setMessage({ type: '', text: '' });

      let response;
      if (type === 'friday') {
        response = await sendFridayPickReminder();
      } else if (type === 'saturday') {
        response = await sendSaturdayPickReminder();
      } else {
        throw new Error(`Unknown reminder type: ${type}`);
      }

      const result = response.data;
      setMessage({
        type: 'success',
        text: `${type.charAt(0).toUpperCase() + type.slice(1)} reminder sent successfully! ${result.result?.totalUsersReminded || result.result?.userCount || 0} users reminded.`
      });
    } catch (error) {
      console.error(`Error sending ${type} reminder:`, error);
      setMessage({ type: 'error', text: `Failed to send ${type} reminder: ${error.response?.data?.error || error.message}` });
    } finally {
      setSendingReminder(prev => ({ ...prev, [type]: false }));
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading Discord settings...</div>;
  }

  return (
    <div className={styles.container}>
      <h2>Discord Channel Management</h2>

      {message.text && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          {message.text}
        </div>
      )}

      <div className={styles.section}>
        <h3>Server invite link</h3>
        <p className={styles.sectionDescription}>
          Shown as “Join the LOTW server” on the first-login tour. Leave blank to hide that button.
          Create a never-expiring invite in Discord (or a custom vanity URL) so this does not rot.
        </p>
        <form onSubmit={handleSaveInvite} className={styles.inviteForm}>
          <div className={styles.inputGroup}>
            <label htmlFor="serverInviteUrl">Invite URL</label>
            <input
              type="url"
              id="serverInviteUrl"
              value={inviteUrl}
              onChange={(e) => setInviteUrl(e.target.value)}
              placeholder="https://discord.gg/your-invite"
            />
          </div>
          <button type="submit" disabled={savingInvite} className={styles.addButton}>
            {savingInvite ? 'Saving...' : 'Save invite'}
          </button>
        </form>
      </div>

      {/* Add New Channel */}
      <div className={styles.section}>
        <h3>Add New Channel</h3>
        <form onSubmit={handleAddChannel} className={styles.addChannelForm}>
          <div className={styles.inputGroup}>
            <label htmlFor="channelId">Discord Channel ID:</label>
            <input
              type="text"
              id="channelId"
              value={newChannel.channelId}
              onChange={(e) => setNewChannel({ ...newChannel, channelId: e.target.value })}
              placeholder="e.g., 123456789012345678"
              required
            />
          </div>
          <div className={styles.inputGroup}>
            <label htmlFor="displayName">Display Name:</label>
            <input
              type="text"
              id="displayName"
              value={newChannel.displayName}
              onChange={(e) => setNewChannel({ ...newChannel, displayName: e.target.value })}
              placeholder="e.g., General Broadcast"
              required
            />
          </div>
          <button type="submit" disabled={saving} className={styles.addButton}>
            {saving ? 'Adding...' : 'Add Channel'}
          </button>
        </form>
      </div>

      {/* Channel List */}
      <div className={styles.section}>
        <h3>Configured Channels</h3>
        {channels.length === 0 ? (
          <p className={styles.noChannels}>No channels configured yet.</p>
        ) : (
          <div className={styles.channelList}>
            {channels.map(channel => (
              <div key={channel.id} className={styles.channelItem}>
                <div className={styles.channelInfo}>
                  <div className={styles.channelHeader}>
                    <h4>{channel.display_name}</h4>
                    <div className={styles.channelActions}>
                      <button
                        onClick={() => handleUpdateChannelName(channel.id)}
                        disabled={saving}
                        className={styles.updateNameButton}
                        title="Update channel name from Discord"
                      >
                        🔄
                      </button>
                      <button
                        onClick={() => setEditingChannel(editingChannel === channel.id ? null : channel.id)}
                        className={styles.editButton}
                      >
                        {editingChannel === channel.id ? 'Cancel' : 'Edit'}
                      </button>
                      <button
                        onClick={() => handleDeleteChannel(channel.id)}
                        disabled={saving}
                        className={styles.deleteButton}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className={styles.channelDetails}>
                    <p><strong>Channel ID:</strong> {channel.channel_id}</p>
                    {channel.channel_name && (
                      <p><strong>Discord Name:</strong> #{channel.channel_name}</p>
                    )}
                  </div>
                </div>

                {editingChannel === channel.id && (
                  <div className={styles.editForm}>
                    <div className={styles.inputGroup}>
                      <label>Display Name:</label>
                      <input
                        type="text"
                        value={channel.display_name}
                        onChange={(e) => {
                          const updatedChannel = { ...channel, display_name: e.target.value };
                          setChannels(channels.map(c => c.id === channel.id ? updatedChannel : c));
                        }}
                      />
                    </div>
                    <div className={styles.editActions}>
                      <button
                        onClick={() => handleUpdateChannel(channel.id, {
                          displayName: channel.display_name
                        })}
                        disabled={saving}
                        className={styles.saveButton}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Broadcast Mappings */}
      <div className={styles.section}>
        <h3>Broadcast Type Mappings</h3>
        <p className={styles.sectionDescription}>
          Configure which channel each type of broadcast should be sent to.
          Leave unselected to disable that broadcast type.
        </p>

        <div className={styles.mappingList}>
          {broadcastTypes.map(broadcastType => (
            <div key={broadcastType.key} className={styles.mappingItem}>
              <div className={styles.mappingInfo}>
                <h4>{broadcastType.name}</h4>
                <p>{broadcastType.description}</p>
              </div>
              <div className={styles.mappingControl}>
                <select
                  value={broadcastMappings[broadcastType.key]?.channel_id || ''}
                  onChange={(e) => handleBroadcastMappingChange(broadcastType.key, e.target.value)}
                  disabled={saving}
                >
                  <option value="">-- Disabled --</option>
                  {channels.map(channel => (
                    <option key={channel.id} value={channel.channel_id}>
                      {channel.display_name}
                    </option>
                  ))}
                </select>
                {broadcastMappings[broadcastType.key] && (
                  <span className={styles.currentMapping}>
                    Currently: {getChannelDisplayName(broadcastMappings[broadcastType.key].channel_id)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pick Reminders */}
      <div className={styles.section}>
        <h3>Pick Reminders</h3>
        <div className={styles.reminderContainer}>
          <div className={styles.reminderInfo}>
            <p>Send pick reminders to users who haven't submitted locks for the current week.</p>
            <p><strong>Automatic Schedule:</strong></p>
            <ul>
              <li>Friday 9:00 PM CST - Friday Night Reminder</li>
              <li>Saturday 10:30 AM CST - Gameday Reminder</li>
            </ul>
          </div>
          <div className={styles.reminderButtons}>
            <button
              className={styles.reminderButton}
              onClick={() => sendPickReminder('friday')}
              disabled={sendingReminder.friday}
            >
              {sendingReminder.friday ? 'Sending...' : 'Send Friday Reminder'}
            </button>
            <button
              className={styles.reminderButton}
              onClick={() => sendPickReminder('saturday')}
              disabled={sendingReminder.saturday}
            >
              {sendingReminder.saturday ? 'Sending...' : 'Send Saturday Reminder'}
            </button>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className={styles.section}>
        <h3>How to Get Discord Channel IDs</h3>
        <div className={styles.helpContent}>
          <ol>
            <li>Enable Developer Mode in Discord (User Settings → Advanced → Developer Mode)</li>
            <li>Right-click on the channel you want to use</li>
            <li>Select "Copy ID" from the context menu</li>
            <li>Paste the ID into the Channel ID field above</li>
          </ol>
          <p><strong>Note:</strong> The bot must have access to the channel and permission to send messages.</p>
        </div>
      </div>
    </div>
  );
};

export default DiscordChannelManager;
